import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CategoryFilter from './components/CategoryFilter';
import SearchBar from './components/SearchBar';
import MenuItemCard from './components/MenuItemCard';
import IngredientGlossary from './components/IngredientGlossary';
import SeasonalHighlight from './components/SeasonalHighlight';
import BakingProcess from './components/BakingProcess';

const MenuShowcase = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dietaryFilter, setDietaryFilter] = useState('all');

  const categories = [
  { id: 'all', name: 'All Items', icon: 'Utensils', count: 48 },
  { id: 'pastries', name: 'Pastries', icon: 'Croissant', count: 15 },
  { id: 'breads', name: 'Breads', icon: 'Wheat', count: 12 },
  { id: 'sandwiches', name: 'Sandwiches', icon: 'Sandwich', count: 10 },
  { id: 'gelato', name: 'Gelato', icon: 'IceCream', count: 11 }];


  const menuItems = [
  {
    id: 1,
    name: "Classic Cornetto",
    italianName: "Cornetto Classico",
    category: "pastries",
    price: 3.50,
    image: "https://images.unsplash.com/photo-1600930496627-33491158a923",
    imageAlt: "Golden flaky Italian croissant cornetto with delicate layers on white ceramic plate with espresso cup",
    description: "Traditional Italian breakfast pastry with delicate, flaky layers and a hint of sweetness. Baked fresh every morning at 5 AM.",
    prepTime: "Fresh daily at 5 AM",
    ingredients: "Italian 00 flour, European butter, organic eggs, raw cane sugar",
    story: "Our nonna's recipe from Naples, passed down through four generations of master bakers.",
    dietary: [
    { label: "Contains Gluten", icon: "Wheat" },
    { label: "Contains Dairy", icon: "Milk" }],

    isNew: false,
    isSeasonal: false
  },
  {
    id: 2,
    name: "Pistachio Cream Cornetto",
    italianName: "Cornetto alla Crema di Pistacchio",
    category: "pastries",
    price: 4.75,
    image: "https://images.unsplash.com/photo-1730426373073-97e7b92c71c4",
    imageAlt: "Flaky croissant filled with vibrant green pistachio cream drizzling from center on rustic wooden board",
    description: "Buttery cornetto filled with authentic Sicilian pistachio cream made from Bronte pistachios. A customer favorite since 2018.",
    prepTime: "Fresh daily at 5 AM",
    ingredients: "Bronte pistachios, Italian 00 flour, European butter, organic cream",
    story: "Made with pistachios from the volcanic slopes of Mount Etna in Sicily, known as 'green gold'.",
    dietary: [
    { label: "Contains Gluten", icon: "Wheat" },
    { label: "Contains Dairy", icon: "Milk" },
    { label: "Contains Nuts", icon: "Nut" }],

    isNew: false,
    isSeasonal: false
  },
  {
    id: 3,
    name: "Sfogliatella Riccia",
    italianName: "Sfogliatella Napoletana",
    category: "pastries",
    price: 4.25,
    image: "https://images.unsplash.com/photo-1727041425496-bacd1915fac5",
    imageAlt: "Traditional shell-shaped Italian pastry with crispy golden layers and sweet ricotta filling on marble surface",
    description: "Shell-shaped Neapolitan pastry with 144 paper-thin layers and sweet ricotta filling with candied citrus. A labor of love.",
    prepTime: "Made fresh daily",
    ingredients: "Ricotta cheese, semolina, candied orange, cinnamon, Italian 00 flour",
    story: "Originated in a monastery near Amalfi in the 17th century. Each one takes 3 days to prepare properly.",
    dietary: [
    { label: "Contains Gluten", icon: "Wheat" },
    { label: "Contains Dairy", icon: "Milk" }],

    isNew: false,
    isSeasonal: false
  },
  {
    id: 4,
    name: "Panettone Tradizionale",
    italianName: "Panettone di Milano",
    category: "pastries",
    price: 28.00,
    image: "https://images.unsplash.com/photo-1685983258847-6605caaaaa60",
    imageAlt: "Tall dome-shaped Italian Christmas bread with candied fruits and raisins in traditional paper wrapper",
    description: "Traditional Milanese Christmas bread with natural yeast starter, candied fruits, and raisins. 36-hour fermentation process.",
    prepTime: "3-day preparation",
    ingredients: "Natural yeast starter (lievito madre), candied orange, sultana raisins, Italian butter",
    story: "Our lievito madre starter is over 40 years old, maintained daily with the same care as a family member.",
    dietary: [
    { label: "Contains Gluten", icon: "Wheat" },
    { label: "Contains Dairy", icon: "Milk" }],

    isNew: false,
    isSeasonal: true
  },
  {
    id: 5,
    name: "Ciabatta Rustica",
    italianName: "Ciabatta Artigianale",
    category: "breads",
    price: 5.50,
    image: "https://images.unsplash.com/photo-1592916886625-ba0beec93f1a",
    imageAlt: "Rustic Italian slipper bread with crispy golden crust and airy interior holes on linen cloth",
    description: "Rustic Italian slipper bread with crispy crust and open, airy crumb. Perfect for sandwiches or dipping in olive oil.",
    prepTime: "Baked twice daily",
    ingredients: "Italian 00 flour, extra virgin olive oil, sea salt, natural yeast",
    story: "Originated in Veneto in 1982 as a response to French baguettes. Ours uses a 24-hour cold fermentation.",
    dietary: [
    { label: "Contains Gluten", icon: "Wheat" },
    { label: "Vegan", icon: "Leaf" }],

    isNew: false,
    isSeasonal: false
  },
  {
    id: 6,
    name: "Focaccia Genovese",
    italianName: "Focaccia di Genova",
    category: "breads",
    price: 6.75,
    image: "https://images.unsplash.com/photo-1644100744282-d9e6f1a00f83",
    imageAlt: "Golden flatbread with dimpled surface glistening with olive oil and sea salt crystals on wooden board",
    description: "Traditional Ligurian flatbread with extra virgin olive oil, sea salt, and rosemary. Soft, pillowy interior with golden crust.",
    prepTime: "Fresh every 3 hours",
    ingredients: "Ligurian olive oil, Italian 00 flour, fresh rosemary, Maldon sea salt",
    story: "Made using the traditional Genoese method with olive oil brine that creates the signature dimples.",
    dietary: [
    { label: "Contains Gluten", icon: "Wheat" },
    { label: "Vegan", icon: "Leaf" }],

    isNew: false,
    isSeasonal: false
  },
  {
    id: 7,
    name: "Pane di Altamura",
    italianName: "Pane di Altamura DOP",
    category: "breads",
    price: 8.50,
    image: "https://images.unsplash.com/photo-1596662894155-8196e1599aec",
    imageAlt: "Large round artisan bread loaf with thick golden crust and rustic scoring on burlap fabric",
    description: "Protected designation bread from Puglia made with durum wheat semolina. Stays fresh for up to a week.",
    prepTime: "24-hour fermentation",
    ingredients: "Durum wheat semolina, natural yeast, sea salt, spring water",
    story: "This bread has been made in Altamura for over 2000 years. Our recipe follows DOP specifications exactly.",
    dietary: [
    { label: "Contains Gluten", icon: "Wheat" },
    { label: "Vegan", icon: "Leaf" }],

    isNew: false,
    isSeasonal: false
  },
  {
    id: 8,
    name: "Prosciutto e Mozzarella",
    italianName: "Panino al Prosciutto",
    category: "sandwiches",
    price: 12.50,
    image: "https://images.unsplash.com/photo-1504964149979-7a695affe978",
    imageAlt: "Italian sandwich with thin sliced prosciutto and fresh mozzarella on crusty ciabatta with arugula",
    description: "Imported Parma prosciutto, fresh buffalo mozzarella, arugula, and balsamic glaze on our house-made ciabatta.",
    prepTime: "Made to order",
    ingredients: "24-month aged Parma prosciutto, buffalo mozzarella, wild arugula, aged balsamic",
    story: "Our prosciutto comes from the same producer in Parma that our family has worked with for 35 years.",
    dietary: [
    { label: "Contains Gluten", icon: "Wheat" },
    { label: "Contains Dairy", icon: "Milk" }],

    isNew: false,
    isSeasonal: false
  },
  {
    id: 9,
    name: "Caprese Classico",
    italianName: "Panino Caprese",
    category: "sandwiches",
    price: 11.00,
    image: "https://images.unsplash.com/photo-1641898378629-de9424086ad0",
    imageAlt: "Fresh mozzarella and tomato sandwich with basil leaves on toasted focaccia bread drizzled with olive oil",
    description: "Fresh mozzarella di bufala, heirloom tomatoes, basil, and extra virgin olive oil on toasted focaccia.",
    prepTime: "Made to order",
    ingredients: "Buffalo mozzarella, San Marzano tomatoes, fresh basil, Ligurian olive oil",
    story: "Named after the island of Capri, this simple sandwich showcases the quality of each ingredient.",
    dietary: [
    { label: "Contains Gluten", icon: "Wheat" },
    { label: "Contains Dairy", icon: "Milk" },
    { label: "Vegetarian", icon: "Leaf" }],

    isNew: false,
    isSeasonal: false
  },
  {
    id: 10,
    name: "Porchetta Romana",
    italianName: "Panino con Porchetta",
    category: "sandwiches",
    price: 13.50,
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b1b70543-1764538642219.png",
    imageAlt: "Sliced roasted pork porchetta with crispy skin and herbs on crusty Italian roll with greens",
    description: "Slow-roasted pork porchetta with fennel, garlic, and herbs, served on crusty rosetta roll with salsa verde.",
    prepTime: "Made to order",
    ingredients: "Heritage pork shoulder, wild fennel, garlic, rosemary, sage",
    story: "Roasted for 8 hours using a centuries-old Roman recipe. The crispy skin is the most prized part.",
    dietary: [
    { label: "Contains Gluten", icon: "Wheat" }],

    isNew: false,
    isSeasonal: false
  },
  {
    id: 11,
    name: "Pistachio Gelato",
    italianName: "Gelato al Pistacchio",
    category: "gelato",
    price: 6.50,
    image: "https://images.unsplash.com/photo-1630859885680-8f780e2e65a9",
    imageAlt: "Creamy green pistachio gelato in traditional metal container with fresh pistachios scattered around",
    description: "Made with 40% Bronte pistachios from Sicily. Rich, creamy, and intensely nutty with no artificial colors.",
    prepTime: "Made fresh daily",
    ingredients: "Bronte pistachios, whole milk, organic cream, raw cane sugar",
    story: "We use only Bronte pistachios, which grow on volcanic soil and have a unique, intense flavor.",
    dietary: [
    { label: "Contains Dairy", icon: "Milk" },
    { label: "Contains Nuts", icon: "Nut" },
    { label: "Gluten-Free", icon: "Check" }],

    isNew: false,
    isSeasonal: false
  },
  {
    id: 12,
    name: "Stracciatella Gelato",
    italianName: "Gelato alla Stracciatella",
    category: "gelato",
    price: 6.00,
    image: "https://images.unsplash.com/photo-1657622622114-9bedd276776f",
    imageAlt: "Creamy white gelato with dark chocolate shavings swirled throughout in glass serving bowl",
    description: "Sweet cream gelato with hand-shaved dark chocolate ribbons. Simple perfection in every spoonful.",
    prepTime: "Made fresh daily",
    ingredients: "Whole milk, organic cream, 70% dark chocolate, Madagascar vanilla",
    story: "Invented in Bergamo in 1961. We shave the chocolate by hand while the gelato is still soft.",
    dietary: [
    { label: "Contains Dairy", icon: "Milk" },
    { label: "Gluten-Free", icon: "Check" }],

    isNew: false,
    isSeasonal: false
  },
  {
    id: 13,
    name: "Hazelnut Gelato",
    italianName: "Gelato alla Nocciola",
    category: "gelato",
    price: 6.50,
    image: "https://images.unsplash.com/photo-1668146348649-926cf97ae30c",
    imageAlt: "Rich brown hazelnut gelato with roasted hazelnuts on top in traditional Italian gelato display",
    description: "Made with Piedmont hazelnuts (Nocciola del Piemonte IGP). Roasted in-house for maximum flavor.",
    prepTime: "Made fresh daily",
    ingredients: "Piedmont hazelnuts, whole milk, organic cream, raw cane sugar",
    story: "Piedmont hazelnuts are considered the finest in the world. We roast them fresh each morning.",
    dietary: [
    { label: "Contains Dairy", icon: "Milk" },
    { label: "Contains Nuts", icon: "Nut" },
    { label: "Gluten-Free", icon: "Check" }],

    isNew: false,
    isSeasonal: false
  },
  {
    id: 14,
    name: "Lemon Sorbetto",
    italianName: "Sorbetto al Limone",
    category: "gelato",
    price: 5.50,
    image: "https://images.unsplash.com/photo-1650553621589-812595f4cfbc",
    imageAlt: "Bright yellow lemon sorbet in frozen lemon shell with fresh mint leaves and lemon slices",
    description: "Refreshing dairy-free sorbetto made with Amalfi Coast lemons. Perfectly tart and sweet.",
    prepTime: "Made fresh daily",
    ingredients: "Amalfi lemons, organic cane sugar, spring water, lemon zest",
    story: "Traditional palate cleanser between courses. We use the entire lemon including the zest for maximum flavor.",
    dietary: [
    { label: "Dairy-Free", icon: "Leaf" },
    { label: "Vegan", icon: "Leaf" },
    { label: "Gluten-Free", icon: "Check" }],

    isNew: false,
    isSeasonal: false
  },
  {
    id: 15,
    name: "Tiramisu Gelato",
    italianName: "Gelato al Tiramisù",
    category: "gelato",
    price: 7.00,
    image: "https://images.unsplash.com/photo-1712262582604-4cbafeeca156",
    imageAlt: "Layered coffee and cream gelato with cocoa powder dusting resembling classic tiramisu dessert",
    description: "Coffee-infused gelato with mascarpone cream swirls and cocoa powder. Our most popular flavor.",
    prepTime: "Made fresh daily",
    ingredients: "Espresso, mascarpone, ladyfinger cookies, cocoa powder, Marsala wine",
    story: "Based on the classic Venetian dessert. We use our own espresso blend for authentic coffee flavor.",
    dietary: [
    { label: "Contains Dairy", icon: "Milk" },
    { label: "Contains Gluten", icon: "Wheat" },
    { label: "Contains Alcohol", icon: "Wine" }],

    isNew: true,
    isSeasonal: false
  }];


  const ingredientGlossary = [
  {
    id: 1,
    name: "Lievito Madre",
    pronunciation: "lee-eh-VEE-toh MAH-dreh",
    description: "Natural yeast starter maintained through daily feeding. Our starter is over 40 years old and gives our breads their distinctive flavor and texture. It requires daily care and attention, much like a family member.",
    origin: "Northern Italy, traditional to Lombardy region"
  },
  {
    id: 2,
    name: "Farina 00",
    pronunciation: "fah-REE-nah ZEH-roh ZEH-roh",
    description: "Ultra-fine Italian flour milled to a powder-like consistency. The '00' refers to the fineness of the grind. Essential for authentic Italian pastries and pasta, it creates a delicate, tender crumb.",
    origin: "Throughout Italy, milled from soft wheat varieties"
  },
  {
    id: 3,
    name: "Pistacchio di Bronte",
    pronunciation: "pee-STAHK-kee-oh dee BROHN-teh",
    description: "Prized pistachios grown on the volcanic slopes of Mount Etna in Sicily. Known as 'green gold,' they have an intense, unique flavor and vibrant green color. Harvested only every two years.",
    origin: "Bronte, Sicily - Protected Designation of Origin (DOP)"
  },
  {
    id: 4,
    name: "Mozzarella di Bufala",
    pronunciation: "moht-tsah-REHL-lah dee BOO-fah-lah",
    description: "Fresh cheese made from water buffalo milk. Creamier and more flavorful than cow\'s milk mozzarella. Must be consumed within days of production for optimal taste and texture.",
    origin: "Campania region, particularly around Naples"
  },
  {
    id: 5,
    name: "Prosciutto di Parma",
    pronunciation: "proh-SHOO-toh dee PAHR-mah",
    description: "Dry-cured ham aged for a minimum of 12 months (ours is 24 months). Made from specially bred pigs fed a controlled diet. The curing process uses only sea salt and time.",
    origin: "Parma, Emilia-Romagna - Protected Designation of Origin (DOP)"
  }];


  const seasonalHighlight = {
    title: "Winter Seasonal Special",
    availableUntil: "March 15, 2025",
    itemName: "Panettone Tradizionale",
    italianName: "Panettone di Milano",
    price: 28.00,
    image: "https://images.unsplash.com/photo-1685983258847-6605caaaaa60",
    imageAlt: "Tall dome-shaped Italian Christmas bread with candied fruits and raisins in traditional paper wrapper on festive table",
    description: "Our traditional Milanese Christmas bread is a labor of love requiring 3 days of preparation. Made with our 40-year-old natural yeast starter (lievito madre), premium Italian butter, and hand-selected candied fruits. The 36-hour fermentation process creates the signature light, airy texture and complex flavor that makes authentic panettone so special. Each loaf is hand-wrapped in traditional paper and tied with ribbon.",
    tradition: "Panettone originated in Milan in the 15th century and has become the symbol of Italian Christmas celebrations. Traditionally enjoyed with a glass of sweet wine or hot chocolate, it's given as a gift to friends and family during the holiday season. Our recipe follows the strict Milanese tradition, using only natural leavening and no preservatives."
  };

  const bakingProcess = {
    masterNote: "Every morning at 3 AM, our master bakers begin the day by checking our lievito madre starter and preparing the first batch of cornetti. By 5 AM, the aroma of fresh baking fills the neighborhood. This daily ritual has remained unchanged for over 35 years.",
    steps: [
    {
      id: 1,
      title: "Starter Preparation",
      description: "Our 40-year-old lievito madre is fed daily with fresh flour and water. This natural yeast gives our breads their distinctive flavor and helps them stay fresh longer.",
      duration: "12-24 hours",
      image: "https://images.unsplash.com/photo-1547648036-f8c7396b8718",
      imageAlt: "Baker's hands kneading sourdough starter in large ceramic bowl with flour dusting wooden work surface"
    },
    {
      id: 2,
      title: "Dough Development",
      description: "We use traditional hand-kneading techniques passed down through generations. The dough is folded and rested multiple times to develop the perfect texture and gluten structure.",
      duration: "2-3 hours",
      image: "https://images.unsplash.com/photo-1626202658580-32c4bf14e1ba",
      imageAlt: "Professional baker in white apron hand-kneading smooth dough on marble counter in traditional bakery"
    },
    {
      id: 3,
      title: "Shaping & Proofing",
      description: "Each pastry is carefully shaped by hand. The shaped dough then rests in our temperature-controlled proofing room, where it slowly rises to develop complex flavors.",
      duration: "1-2 hours",
      image: "https://images.unsplash.com/photo-1483695028939-5bb13f8648b0",
      imageAlt: "Rows of shaped croissants proofing on linen-lined baking trays in professional bakery proofing room"
    },
    {
      id: 4,
      title: "Baking to Perfection",
      description: "Our stone deck ovens maintain precise temperatures for even baking. We bake in small batches throughout the day to ensure everything is fresh. The golden color and flaky texture come from careful temperature control.",
      duration: "15-25 minutes",
      image: "https://images.unsplash.com/photo-1704500390269-1b226c48d130",
      imageAlt: "Golden pastries baking in traditional stone deck oven with baker checking temperature and color"
    }]

  };

  const filteredItems = useMemo(() => {
    return menuItems?.filter((item) => {
      const matchesCategory = activeCategory === 'all' || item?.category === activeCategory;
      const matchesSearch = searchTerm === '' ||
      item?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      item?.italianName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      item?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      item?.ingredients?.toLowerCase()?.includes(searchTerm?.toLowerCase());

      const matchesDietary = dietaryFilter === 'all' ||
      item?.dietary?.some((d) => {
        if (dietaryFilter === 'gluten-free') return d?.label === 'Gluten-Free';
        if (dietaryFilter === 'vegan') return d?.label === 'Vegan';
        if (dietaryFilter === 'dairy-free') return d?.label === 'Dairy-Free';
        if (dietaryFilter === 'nut-free') return !d?.label?.includes('Nut');
        return true;
      });

      return matchesCategory && matchesSearch && matchesDietary;
    });
  }, [activeCategory, searchTerm, dietaryFilter]);

  return (
    <>
      <Helmet>
        <title>Menu Showcase - Authentic Italian Pastries, Breads & More | Montesanto Bakery</title>
        <meta name="description" content="Explore our authentic Italian menu featuring fresh pastries, artisan breads, gourmet sandwiches, and handcrafted gelato. Made daily with traditional techniques and premium ingredients." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-20">
          <section className="bg-gradient-to-br from-primary/10 to-accent/10 py-16">
            <div className="container-custom">
              <div className="max-w-3xl">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-14 h-14 bg-primary/20 rounded-lg flex items-center justify-center">
                    <Icon name="UtensilsCrossed" size={28} color="var(--color-primary)" />
                  </div>
                  <div>
                    <h1 className="font-serif text-4xl md:text-5xl font-semibold text-foreground">
                      Our Artisan Menu
                    </h1>
                    <p className="text-muted-foreground">
                      Freshly baked with heart, every single day
                    </p>
                  </div>
                </div>
                <p className="text-lg text-foreground leading-relaxed">
                  Every item on our menu tells a story of Italian tradition, crafted with authentic techniques passed down through generations. From our 5 AM fresh cornetti to our handcrafted gelato, we use only the finest imported ingredients and time-honored methods.
                </p>
              </div>
            </div>
          </section>

          <section className="py-12">
            <div className="container-custom">
              <SeasonalHighlight seasonal={seasonalHighlight} />
            </div>
          </section>

          <section className="py-12 bg-muted/30">
            <div className="container-custom space-y-6">
              <CategoryFilter
                categories={categories}
                activeCategory={activeCategory}
                onCategoryChange={setActiveCategory} />

              
              <SearchBar
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                dietaryFilter={dietaryFilter}
                onDietaryChange={setDietaryFilter} />

            </div>
          </section>

          <section className="py-12">
            <div className="container-custom">
              {filteredItems?.length > 0 ?
              <>
                  <div className="flex items-center justify-between mb-8">
                    <h2 className="font-serif text-2xl font-semibold text-foreground">
                      {activeCategory === 'all' ? 'All Items' : categories?.find((c) => c?.id === activeCategory)?.name}
                    </h2>
                    <span className="text-sm text-muted-foreground">
                      {filteredItems?.length} {filteredItems?.length === 1 ? 'item' : 'items'} found
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredItems?.map((item) =>
                  <MenuItemCard key={item?.id} item={item} />
                  )}
                  </div>
                </> :

              <div className="text-center py-16">
                  <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="Search" size={32} color="var(--color-muted-foreground)" />
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-foreground mb-2">
                    No items found
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    Try adjusting your filters or search terms
                  </p>
                  <Button
                  variant="outline"
                  onClick={() => {
                    setActiveCategory('all');
                    setSearchTerm('');
                    setDietaryFilter('all');
                  }}>

                    Clear all filters
                  </Button>
                </div>
              }
            </div>
          </section>

          <section className="py-12 bg-muted/30">
            <div className="container-custom">
              <BakingProcess process={bakingProcess} />
            </div>
          </section>

          <section className="py-12">
            <div className="container-custom">
              <IngredientGlossary ingredients={ingredientGlossary} />
            </div>
          </section>

          <section className="py-16 bg-gradient-to-br from-primary/10 to-accent/10">
            <div className="container-custom text-center">
              <div className="max-w-2xl mx-auto">
                <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Icon name="ShoppingBag" size={32} color="var(--color-primary)" />
                </div>
                <h2 className="font-serif text-3xl font-semibold text-foreground mb-4">
                  Ready to Order?
                </h2>
                <p className="text-lg text-muted-foreground mb-8">
                  Visit us in Etobicoke or place your order for pickup. We're open daily with fresh items baked throughout the day.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button variant="default" size="lg" iconName="MapPin" iconPosition="left">
                    Visit Our Bakery
                  </Button>
                  <Button variant="outline" size="lg" iconName="Phone" iconPosition="left">
                    Call to Order
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>);

};

export default MenuShowcase;